import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  Bell,
  Building,
  ClipboardList,
  Loader2,
  Mail,
  Monitor,
  Settings,
  UserCog,
  Users,
  Wrench,
} from 'lucide-react';
import { api, extractErrorMessage } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { PdfExportButton } from '../components/shared/PdfExportButton';

/**
 * Page `/personnels/:id` — fiche détaillée 360° d'un personnel (US-PERS-03).
 *
 * Sections :
 *  - Contact (avatar, nom, email)
 *  - Organisation (rôle, statut, dernière connexion)
 *  - Responsabilités (services dont il est responsable — placeholder Phase 9)
 *  - Équipements affectés (placeholder Phase 10)
 *  - Interventions (placeholder Phase 11)
 *  - Notifications récentes (uniquement pour son propre profil)
 *  - Sessions actives (lecture seule)
 *
 * Pas de CRUD ici (cf. US-ADM-02 → /admin/utilisateurs).
 */

interface PersonnelDetail {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  avatar_signed_url: string | null;
  role_id: string | null;
  service_id: string | null;
  is_active: boolean;
  locked_until: string | null;
  last_login_at: string | null;
  created_at: string;
  custom_fields: Record<string, unknown>;
  sessions: Array<{
    id: string;
    ip: string | null;
    user_agent: string | null;
    created_at: string;
    last_seen_at: string;
    expires_at: string;
  }>;
  notifications: Array<{
    id: string;
    title: string;
    message: string;
    type: string;
    priority: string;
    is_read: boolean;
    created_at: string;
    action_url: string | null;
  }>;
  services_responsable: Array<{ id: string; nom: string; role: string }>;
  equipements_affectes: Array<{
    id: string;
    code: string;
    libelle: string;
    statut: string;
    statut_affectation: string | null;
  }>;
  interventions: {
    demandes_creees: Array<{
      id: string;
      numero_demande: string;
      titre: string;
      priorite: string;
      statut: string;
    }>;
    affectees: Array<{
      id: string;
      numero_demande: string;
      titre: string;
      priorite: string;
      statut: string;
    }>;
    validations_en_attente: Array<{
      validation_id: string;
      type_validation: string;
      intervention_id: string;
      numero_demande: string;
      titre: string;
      priorite: string;
    }>;
    cloturees_count: number;
    note_moyenne: number | null;
  };
}

interface RoleOption {
  id: string;
  name: string;
}

export function PersonnelDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user: currentUser } = useAuth();
  const [detail, setDetail] = useState<PersonnelDetail | null>(null);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const pdfRef = useRef<HTMLDivElement>(null);

  const isSelf = currentUser?.id === id;

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const [d, r] = await Promise.all([
          api.get<{ data: PersonnelDetail }>(`/personnels/${id}`),
          api.get<{ data: RoleOption[] }>('/admin/roles').catch(() => ({ data: { data: [] } })),
        ]);
        setDetail(d.data.data);
        setRoles(r.data.data);
      } catch (err) {
        setErrorMessage(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (errorMessage || !detail) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage ?? 'Personnel introuvable'}</p>
        </div>
        <Link
          to="/personnels"
          className="inline-flex items-center mt-4 text-sm text-primary-600 hover:text-primary-700"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Retour à l'annuaire
        </Link>
      </div>
    );
  }

  const role = roles.find((r) => r.id === detail.role_id);
  const locked = detail.locked_until && new Date(detail.locked_until) > new Date();

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Link
          to="/personnels"
          className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Retour à l'annuaire
        </Link>
        <div className="flex items-center gap-2">
          <PdfExportButton
            targetRef={pdfRef}
            filename={`fiche-${detail.last_name}-${detail.first_name}.pdf`}
          />
          {isSelf && (
            <Link
              to="/profil"
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <UserCog className="w-4 h-4 mr-2" />
              Modifier mon profil
            </Link>
          )}
        </div>
      </div>

      <div ref={pdfRef} className="space-y-6">
        {/* Identité */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-start space-x-6">
            <div className="flex-shrink-0">
              {detail.avatar_signed_url ? (
                <img
                  src={detail.avatar_signed_url}
                  alt=""
                  className="w-24 h-24 rounded-full object-cover"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-2xl font-bold">
                  {(detail.first_name[0] ?? '') + (detail.last_name[0] ?? '')}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-gray-900">
                {detail.first_name} {detail.last_name}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-gray-600">
                <a
                  href={`mailto:${detail.email}`}
                  className="inline-flex items-center hover:text-primary-600"
                >
                  <Mail className="w-4 h-4 mr-1.5" />
                  {detail.email}
                </a>
                {role && (
                  <span className="inline-flex items-center">
                    <Settings className="w-4 h-4 mr-1.5" />
                    {role.name}
                  </span>
                )}
                <StatusBadge active={detail.is_active} locked={Boolean(locked)} />
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Membre depuis le {new Date(detail.created_at).toLocaleDateString()}
                {detail.last_login_at &&
                  ` · Dernière connexion : ${new Date(detail.last_login_at).toLocaleString()}`}
              </p>
            </div>
          </div>
        </div>

        {/* Grid sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Responsabilités services */}
          <Section icon={Building} title="Services dont il est responsable">
            {detail.services_responsable.length === 0 ? (
              <EmptyHint text="Aucun service sous sa responsabilité." />
            ) : (
              <ul className="space-y-1">
                {detail.services_responsable.map((s) => (
                  <li key={s.id} className="text-sm">
                    <Link to={`/services/${s.id}`} className="text-primary-600 hover:underline">
                      {s.nom}
                    </Link>{' '}
                    <span className="text-gray-500">— {s.role}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/* Équipements affectés */}
          <Section icon={Wrench} title="Équipements affectés">
            {detail.equipements_affectes.length === 0 ? (
              <EmptyHint text="Aucun équipement affecté." />
            ) : (
              <ul className="space-y-1.5">
                {detail.equipements_affectes.map((e) => (
                  <li key={e.id} className="text-sm flex items-baseline gap-2">
                    <Link
                      to={`/equipements/${e.id}`}
                      className="text-primary-600 hover:underline font-medium"
                    >
                      {e.code}
                    </Link>
                    <span className="text-gray-700 truncate">{e.libelle}</span>
                    {e.statut_affectation === 'en_attente_acceptation' && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                        en attente
                      </span>
                    )}
                    {e.statut_affectation === 'refuse' && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-800 border border-red-200">
                        refusé
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/* Interventions */}
          <Section icon={ClipboardList} title="Interventions" className="lg:col-span-2">
            {detail.interventions.demandes_creees.length === 0 &&
            detail.interventions.affectees.length === 0 &&
            detail.interventions.validations_en_attente.length === 0 &&
            detail.interventions.cloturees_count === 0 ? (
              <EmptyHint text="Aucune intervention enregistrée." />
            ) : (
              <div className="space-y-3">
                {detail.interventions.demandes_creees.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase mb-1">
                      Demandes créées en cours ({detail.interventions.demandes_creees.length})
                    </h3>
                    <ul className="space-y-1 text-sm">
                      {detail.interventions.demandes_creees.map((i) => (
                        <li key={i.id} className="flex items-baseline gap-2">
                          <Link
                            to={`/interventions/${i.id}`}
                            className="text-primary-600 hover:underline font-medium"
                          >
                            {i.numero_demande}
                          </Link>
                          <span className="text-gray-700 truncate">{i.titre}</span>
                          <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200">
                            {i.priorite}
                          </span>
                          <span className="text-xs text-gray-500">
                            — {i.statut.replace(/_/g, ' ')}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {detail.interventions.affectees.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase mb-1">
                      Affectées en tant que technicien ({detail.interventions.affectees.length})
                    </h3>
                    <ul className="space-y-1 text-sm">
                      {detail.interventions.affectees.map((i) => (
                        <li key={i.id} className="flex items-baseline gap-2">
                          <Link
                            to={`/interventions/${i.id}`}
                            className="text-primary-600 hover:underline font-medium"
                          >
                            {i.numero_demande}
                          </Link>
                          <span className="text-gray-700 truncate">{i.titre}</span>
                          <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200">
                            {i.priorite}
                          </span>
                          <span className="text-xs text-gray-500">
                            — {i.statut.replace(/_/g, ' ')}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {detail.interventions.validations_en_attente.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase mb-1">
                      Validations en attente ({detail.interventions.validations_en_attente.length})
                    </h3>
                    <ul className="space-y-1 text-sm">
                      {detail.interventions.validations_en_attente.map((v) => (
                        <li key={v.validation_id} className="flex items-baseline gap-2">
                          <Link
                            to={`/interventions/${v.intervention_id}`}
                            className="text-primary-600 hover:underline font-medium"
                          >
                            {v.numero_demande}
                          </Link>
                          <span className="text-gray-700 truncate">{v.titre}</span>
                          <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                            {v.type_validation}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="text-xs text-gray-500">
                  {detail.interventions.cloturees_count} intervention(s) clôturée(s) évaluée(s).
                  {detail.interventions.note_moyenne !== null && (
                    <>
                      {' '}
                      Note moyenne (technicien) : {detail.interventions.note_moyenne.toFixed(2)}/5
                    </>
                  )}
                </p>
              </div>
            )}
          </Section>

          {/* Notifications (uniquement self) */}
          {isSelf && (
            <Section icon={Bell} title="Notifications récentes" className="lg:col-span-2">
              {detail.notifications.length === 0 ? (
                <EmptyHint text="Aucune notification récente." />
              ) : (
                <ul className="divide-y divide-gray-100">
                  {detail.notifications.map((n) => (
                    <li key={n.id} className="py-2 text-sm">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p
                            className={`${n.is_read ? 'text-gray-700' : 'font-semibold text-gray-900'}`}
                          >
                            {n.title}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{n.message}</p>
                        </div>
                        <span className="text-xs text-gray-400 ml-3 flex-shrink-0">
                          {new Date(n.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          )}

          {/* Sessions actives */}
          <Section icon={Monitor} title="Sessions actives" className="lg:col-span-2">
            {detail.sessions.length === 0 ? (
              <EmptyHint text="Aucune session active." />
            ) : (
              <div className="space-y-2">
                {detail.sessions.map((s) => (
                  <div
                    key={s.id}
                    className="rounded-lg border border-gray-200 p-2 text-xs text-gray-700"
                  >
                    <p className="font-medium text-gray-900 truncate">
                      {s.user_agent ?? 'Agent inconnu'}
                    </p>
                    <p className="text-gray-500">
                      IP {s.ip ?? '?'} · ouverte {new Date(s.created_at).toLocaleString()} ·
                      dernière activité {new Date(s.last_seen_at).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Custom fields (Form Builder) */}
          {Object.keys(detail.custom_fields ?? {}).length > 0 && (
            <Section icon={Users} title="Champs personnalisés" className="lg:col-span-2">
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                {Object.entries(detail.custom_fields).map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-gray-100 py-1.5">
                    <dt className="text-gray-500">{k}</dt>
                    <dd className="text-gray-900 font-medium text-right break-all">
                      {v == null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v)}
                    </dd>
                  </div>
                ))}
              </dl>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({
  icon: Icon,
  title,
  children,
  className,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`bg-white rounded-xl shadow-sm border border-gray-200 p-4 ${className ?? ''}`}
    >
      <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
        <Icon className="w-4 h-4 mr-2 text-primary-600" />
        {title}
      </h2>
      {children}
    </section>
  );
}

function EmptyHint({ text, subtext }: { text: string; subtext?: string }) {
  return (
    <div className="text-sm text-gray-500 italic">
      {text}
      {subtext && <span className="block text-xs text-gray-400 mt-0.5">{subtext}</span>}
    </div>
  );
}

function StatusBadge({ active, locked }: { active: boolean; locked: boolean }) {
  if (locked) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        Verrouillé
      </span>
    );
  }
  if (active) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Actif
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
      Désactivé
    </span>
  );
}
